import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { CommandPaletteProvider } from "@/components/shared/CommandPalette";
import { Toaster } from "sonner";
import ContactFAB from "@/components/shared/ContactFAB";
import { AuthProvider } from "@/contexts/AuthContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import {
  organizationJsonLd,
  websiteJsonLd,
  softwareAppJsonLd,
} from "@/components/shared/SEO";
import { Login } from "@/pages/Login";
import { Signup } from "@/pages/Signup";
// Heavy routes are lazy-loaded so the initial bundle doesn't have to ship the
// full editor / marketing / admin UIs before the user needs them. Any page
// with 500+ lines or that pulls in charts / rich-text libs qualifies.
const OnboardingPage = lazy(() => import("@/pages/Onboarding"));
const SuperAdminUsers = lazy(() =>
  import("@/pages/SuperAdminUsers").then((m) => ({ default: m.SuperAdminUsers })),
);
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { Profile } from "@/pages/Profile";
import { AuthCallback } from "@/pages/AuthCallback";
import { LinkedInCallback } from "@/pages/LinkedInCallback";
import { Dashboard } from "@/pages/Dashboard";
import { Products } from "@/pages/Products";
const Library = lazy(() =>
  import("@/pages/Library").then((m) => ({ default: m.Library })),
);
const ProductWizard = lazy(() =>
  import("@/pages/ProductWizard").then((m) => ({ default: m.ProductWizard })),
);
import { ShopifySettings } from "@/pages/ShopifySettings";
const ShopHome = lazy(() =>
  import("@/pages/Shop").then((m) => ({ default: m.ShopHome })),
);
const ShopCategory = lazy(() =>
  import("@/pages/Shop").then((m) => ({ default: m.ShopCategory })),
);
const ShopPDP = lazy(() =>
  import("@/pages/Shop").then((m) => ({ default: m.ShopPDP })),
);
const ShopSearch = lazy(() =>
  import("@/pages/Shop").then((m) => ({ default: m.ShopSearch })),
);
import { WishlistPage } from "@/pages/WishlistPage";
import { SharedWishlistPage } from "@/pages/SharedWishlistPage";
import { ComparePage } from "@/pages/ComparePage";
import { ShopCheckout, ShopOrderConfirm, ShopOrderPending } from "@/pages/ShopCart";
import { ProductDetail } from "@/pages/ProductDetail";
import { UploadContent } from "@/pages/UploadContent";
const Posts = lazy(() =>
  import("@/pages/Posts").then((m) => ({ default: m.Posts })),
);
const Comments = lazy(() =>
  import("@/pages/Comments").then((m) => ({ default: m.Comments })),
);
import { Leads } from "@/pages/Leads";
import { Complaints } from "@/pages/Complaints";
import { Insights } from "@/pages/Insights";
import { Platforms } from "@/pages/Platforms";
import { ChannelInsights } from "@/pages/ChannelInsights";
import { RetryCenter } from "@/pages/RetryCenter";
import { Settings } from "@/pages/Settings";
import AdminSetup from "@/pages/AdminSetup";
import UtmReport from "@/pages/UtmReport";
const Composer = lazy(() => import("@/pages/Composer"));
import Referrals from "@/pages/Referrals";
import Calendar from "@/pages/Calendar";
import Analytics2 from "@/pages/Analytics2";
import { ConsentBanner } from "@/components/ConsentBanner";
import AdminUsersPage from "@/pages/AdminUsers";
import PricingPage from "@/pages/Pricing";
import BillingPage from "@/pages/Billing";
const LandingPage = lazy(() => import("@/pages/Landing"));
import AdminTestimonials from "@/pages/AdminTestimonials";
const AdminSubscriptionsPage = lazy(() => import("@/pages/AdminSubscriptions"));
const AdminPayouts = lazy(() => import("@/pages/AdminPayouts"));
const AdminContactInbox = lazy(() => import("@/pages/AdminContactInbox"));
const AdminFeedback = lazy(() => import("@/pages/AdminFeedback"));
const AdminBillingPlans = lazy(() => import("@/pages/AdminBillingPlans"));
const AdminGift = lazy(() => import("@/pages/AdminGift"));
const AdminGiftProducts = lazy(() => import("@/pages/AdminGiftProducts"));
const GiftPage = lazy(() => import("@/pages/Gift"));
const GiftStatusPage = lazy(() => import("@/pages/GiftStatus"));
const SibaFeedbackPage = lazy(() => import("@/pages/SibaFeedback"));
import { RequireRole } from "@/components/shared/RequireRole";
import { ThemeProvider } from "@/contexts/ThemeContext";
import DiscoveryHome from "@/pages/DiscoveryHome";
import DiscoveryReport from "@/pages/DiscoveryReport";
import DiscoveryTrends from "@/pages/DiscoveryTrends";
const ScoutPage = lazy(() => import("@/pages/Scout"));
import { initAnalytics, usePageviewTracker } from "@/lib/analytics";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import DataDeletion from "@/pages/legal/DataDeletion";
import RefundPolicy from "@/pages/legal/RefundPolicy";
import SecurityPage from "@/pages/legal/SecurityPage";
import DPA from "@/pages/legal/DPA";
import AboutPage from "@/pages/legal/AboutPage";
import AffiliateProgram from "@/pages/legal/AffiliateProgram";
import ContactPage from "@/pages/legal/ContactPage";
import FounderStory from "@/pages/FounderStory";
import CapturedLeads from "@/pages/CapturedLeads";
import LeadCapturePublic from "@/pages/LeadCapturePublic";
import ComingSoon from "@/pages/ComingSoon";
import SellOrders from "@/pages/workspace/SellOrders";
import SellInventory from "@/pages/workspace/SellInventory";
import SellReturns from "@/pages/workspace/SellReturns";
import SellPayouts from "@/pages/workspace/SellPayouts";
import CreditsPage from "@/pages/workspace/CreditsPage";
import WorkspaceModule from "@/pages/workspace/WorkspaceModule";
import CatalogManual from "@/pages/workspace/CatalogManual";
import CatalogBulk from "@/pages/workspace/CatalogBulk";
import IntentRadar from "@/pages/workspace/IntentRadar";
const AICartPage = lazy(() => import("@/pages/AICart"));
const SharedCartPage = lazy(() => import("@/pages/SharedCart"));
import { SanctaChatbot } from "@/components/shared/SanctaChatbot";
import { GuidedTour } from "@/components/shared/GuidedTour";
import SanctaCommerce from "@/pages/SanctaCommerce";
import ChatInbox from "@/pages/ChatInbox";
import NotificationPreferences from "@/pages/NotificationPreferences";
import { useDirectChatNotifier } from "@/hooks/useDirectChatNotifier";
import { useWebPush } from "@/hooks/useWebPush";

// Tiny mount-only component so the notifier hook runs inside AuthProvider.
const DirectChatNotifierMount: React.FC = () => {
  useDirectChatNotifier();
  // Auto-attach a fresh FCM token if the user previously granted push
  // permission — no button click needed on subsequent visits.
  useWebPush({ autoOnGrant: true });
  return null;
};
import { useSociBabaTrackerGlobal, setSociBabaAuthToken } from "@/hooks/useSociBabaTracker";
import { getToken } from "@/lib/api";
import TwilioComposer from "@/pages/workspace/TwilioComposer";
import SignalToAction from "@/pages/workspace/SignalToAction";
import MoneyMeter from "@/pages/workspace/MoneyMeter";
import ViralWaves from "@/pages/workspace/ViralWaves";
import AutoPilot from "@/pages/workspace/AutoPilot";
const SuperAdminDashboard = lazy(() => import("@/pages/workspace/SuperAdminDashboard"));
import SuperAdminModeration from "@/pages/workspace/SuperAdminModeration";
import SuperAdminSupport from "@/pages/workspace/SuperAdminSupport";
import SuperAdminInbox from "@/pages/workspace/SuperAdminInbox";
import SuperAdminAppeals from "@/pages/workspace/SuperAdminAppeals";
import BrandStudio from "@/pages/workspace/BrandStudio";
import AIBlog from "@/pages/workspace/AIBlog";
import HelpCenter from "@/pages/workspace/HelpCenter";
import TicketDetail from "@/pages/workspace/TicketDetail";
import TrendsPublic from "@/pages/public/TrendsPublic";
import ReferralLanding from "@/pages/public/ReferralLanding";
import VotePage from "@/pages/public/VotePage";
import WinsPage from "@/pages/public/WinsPage";
import InstallPrompt from "@/pwa/InstallPrompt";
import NativeBootstrap from "@/pwa/NativeBootstrap";
import { PromoBanner } from "@/components/marketing/PromoBanner";
import { useVisitorIntent } from "@/hooks/useVisitorIntent";

function Router() {
  const location = useLocation();
  usePageviewTracker();
  useSociBabaTrackerGlobal();
  useVisitorIntent();
  // Keep the SociBaba behavioral tracker in sync with the current auth token
  React.useEffect(() => {
    setSociBabaAuthToken(getToken());
    const onStorage = () => setSociBabaAuthToken(getToken());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  // Handle Emergent Google OAuth session_id fragment synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense
      fallback={
        <div
          data-testid="route-suspense-fallback"
          className="fixed inset-0 flex items-center justify-center bg-[color:var(--sg-bg,#fafafa)] dark:bg-neutral-950"
        >
          <div className="flex items-center gap-2 text-sm text-[color:var(--sg-muted,#94a3b8)]">
            <span className="inline-block h-4 w-4 rounded-full border-2 border-[color:var(--sb-saffron,#F97316)] border-t-transparent animate-spin" />
            Loading…
          </div>
        </div>
      }
    >
      <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/superadmin/users" element={<SuperAdminUsers />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/products" element={<Products />} />
      <Route path="/products/new" element={<ProductWizard />} />
      <Route path="/products/:productId/edit" element={<ProductWizard />} />
      <Route path="/library" element={<Library />} />
      <Route path="/score" element={<ScoutPage />} />
      <Route path="/scout" element={<Navigate to="/score" replace />} />
      <Route path="/settings/shopify" element={<ShopifySettings />} />
      {/* Public /shop marketplace */}
      <Route path="/shop" element={<ShopHome />} />
      <Route path="/wishlist" element={<WishlistPage />} />
      <Route path="/wishlist/shared/:token" element={<SharedWishlistPage />} />
      <Route path="/compare" element={<ComparePage />} />
      <Route path="/shop/search" element={<ShopSearch />} />
      <Route path="/shop/c/:category" element={<ShopCategory />} />
      <Route path="/shop/c/:category/:subCategory" element={<ShopCategory />} />
      <Route path="/shop/p/:slug" element={<ShopPDP />} />
      <Route path="/shop/checkout" element={<ShopCheckout />} />
      <Route path="/shop/orders/pending" element={<ShopOrderPending />} />
      <Route path="/shop/order/:orderId" element={<ShopOrderConfirm />} />
      <Route path="/sancta" element={<SanctaCommerce />} />
      <Route path="/messages" element={<ChatInbox role="buyer" />} />
      <Route path="/workspace/inbox" element={<ChatInbox role="seller" />} />
      <Route path="/settings/notifications" element={<NotificationPreferences />} />
      <Route path="/products/:productId" element={<ProductDetail />} />
      <Route path="/upload" element={<UploadContent />} />
      <Route path="/posts" element={<Posts />} />
      <Route path="/composer" element={<Composer />} />
      <Route path="/referrals" element={<Referrals />} />
      <Route path="/calendar" element={<Calendar />} />
      <Route path="/analytics" element={<Analytics2 />} />
      <Route path="/comments" element={<Comments />} />
      <Route path="/leads" element={<Leads />} />
      <Route path="/complaints" element={<Complaints />} />
      <Route path="/lead-capture" element={<CapturedLeads />} />
      {/* Public form — no auth required */}
      <Route path="/contact/:channelId" element={<LeadCapturePublic />} />
      <Route path="/insights" element={<Insights />} />
      <Route path="/platforms" element={<Platforms />} />
      <Route path="/platforms/:accountId/insights" element={<ChannelInsights />} />
      <Route path="/retry" element={<RetryCenter />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/admin/setup" element={<RequireRole superAdminOnly><AdminSetup /></RequireRole>} />
      <Route path="/admin/utm-report" element={<RequireRole superAdminOnly><UtmReport /></RequireRole>} />
      <Route path="/admin/users" element={<RequireRole allow={["admin"]}><AdminUsersPage /></RequireRole>} />
      <Route path="/admin/testimonials" element={<RequireRole superAdminOnly><AdminTestimonials /></RequireRole>} />
      <Route path="/admin/subscriptions" element={<RequireRole superAdminOnly><AdminSubscriptionsPage /></RequireRole>} />
      <Route path="/admin/payouts" element={<RequireRole superAdminOnly><AdminPayouts /></RequireRole>} />
      <Route path="/admin/contact-inbox" element={<RequireRole superAdminOnly><AdminContactInbox /></RequireRole>} />
      <Route path="/admin/feedback" element={<RequireRole superAdminOnly><AdminFeedback /></RequireRole>} />
      <Route path="/admin/billing-plans" element={<RequireRole superAdminOnly><AdminBillingPlans /></RequireRole>} />
      <Route path="/admin/gift" element={<RequireRole superAdminOnly><AdminGift /></RequireRole>} />
      <Route path="/admin/gift-products" element={<RequireRole superAdminOnly><AdminGiftProducts /></RequireRole>} />
      <Route path="/gift" element={<GiftPage />} />
      <Route path="/gift/status" element={<GiftStatusPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/settings/billing" element={<BillingPage />} />
      <Route path="/discovery" element={<DiscoveryHome />} />
      <Route path="/discovery/trends" element={<DiscoveryTrends />} />
      <Route path="/discovery/report/:reportId" element={<DiscoveryReport />} />

      {/* Contest sprint — workspace module placeholders (Block 2-7 fills these) */}
      <Route path="/workspace/ai/signal-to-action" element={<SignalToAction />} />
      <Route path="/workspace/ai/money-meter" element={<MoneyMeter />} />
      <Route path="/workspace/ai/viral-waves" element={<ViralWaves />} />
      <Route path="/workspace/ai/autopilot" element={<AutoPilot />} />
      <Route path="/workspace/ai/brand-studio" element={<BrandStudio />} />
      <Route path="/workspace/ai/blog" element={<AIBlog />} />
      <Route path="/workspace/catalog/manual" element={<CatalogManual />} />
      <Route path="/workspace/catalog/bulk" element={<CatalogBulk />} />
      <Route path="/workspace/catalog/stores" element={<WorkspaceModule slug="catalog-stores" />} />
      <Route path="/workspace/catalog/collections" element={<WorkspaceModule slug="catalog-collections" />} />
      <Route path="/workspace/catalog/media" element={<WorkspaceModule slug="catalog-media" />} />
      <Route path="/workspace/catalog/ai-copy" element={<WorkspaceModule slug="catalog-ai-copy" />} />
      <Route path="/workspace/marketing/whatsapp" element={<TwilioComposer channel="whatsapp" />} />
      <Route path="/workspace/marketing/sms" element={<TwilioComposer channel="sms" />} />
      <Route path="/workspace/marketing/ads" element={<WorkspaceModule slug="marketing-ads" />} />
      <Route path="/workspace/marketing/broadcast" element={<TwilioComposer channel="broadcast" />} />
      <Route path="/workspace/marketing/launch" element={<WorkspaceModule slug="marketing-launch" />} />
      <Route path="/workspace/marketing/shortener" element={<WorkspaceModule slug="marketing-shortener" />} />
      <Route path="/workspace/sell/orders" element={<SellOrders />} />
      <Route path="/workspace/sell/inventory" element={<SellInventory />} />
      <Route path="/workspace/sell/returns" element={<SellReturns />} />
      <Route path="/workspace/sell/payouts" element={<SellPayouts />} />
      <Route path="/workspace/brand/reputation" element={<WorkspaceModule slug="brand-reputation" />} />
      <Route path="/workspace/brand/competitors" element={<WorkspaceModule slug="brand-competitors" />} />
      <Route path="/workspace/brand/sentiment" element={<WorkspaceModule slug="brand-sentiment" />} />
      <Route path="/workspace/brand/insights" element={<WorkspaceModule slug="brand-insights" />} />
      <Route path="/workspace/brand/history" element={<WorkspaceModule slug="brand-history" />} />
      <Route path="/workspace/brand/campaigns" element={<WorkspaceModule slug="brand-campaigns" />} />
      <Route path="/workspace/creator/performance" element={<WorkspaceModule slug="creator-performance" />} />
      <Route path="/workspace/creator/audience" element={<WorkspaceModule slug="creator-audience" />} />
      <Route path="/workspace/creator/topics" element={<WorkspaceModule slug="creator-topics" />} />
      <Route path="/workspace/creator/collabs" element={<WorkspaceModule slug="creator-collabs" />} />
      <Route path="/workspace/clients" element={<WorkspaceModule slug="clients" />} />
      <Route path="/workspace/credits" element={<CreditsPage />} />
      <Route path="/workspace/intent-radar" element={<IntentRadar />} />
      <Route path="/workspace/support/help" element={<HelpCenter />} />
      <Route path="/workspace/support/help/:ticket_id" element={<TicketDetail />} />
      <Route path="/superadmin" element={<RequireRole superAdminOnly><SuperAdminDashboard /></RequireRole>} />
      <Route path="/superadmin/moderation" element={<RequireRole superAdminOnly><SuperAdminModeration /></RequireRole>} />
      <Route path="/superadmin/support" element={<RequireRole superAdminOnly><SuperAdminSupport /></RequireRole>} />
      <Route path="/superadmin/inbox" element={<RequireRole superAdminOnly><SuperAdminInbox /></RequireRole>} />
      <Route path="/superadmin/appeals" element={<RequireRole superAdminOnly><SuperAdminAppeals /></RequireRole>} />

      {/* Public legal / info pages — no auth required */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/data-deletion" element={<DataDeletion />} />
      <Route path="/refunds" element={<RefundPolicy />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/dpa" element={<DPA />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/feedback/sibascore" element={<SibaFeedbackPage />} />
      <Route path="/affiliate" element={<AffiliateProgram />} />
      <Route path="/founder-story" element={<FounderStory />} />

      <Route path="/" element={<LandingPage />} />
      <Route path="/ai-cart" element={<AICartPage />} />
      <Route path="/ai-cart/s/:slug" element={<SharedCartPage />} />
      <Route path="/trends" element={<TrendsPublic />} />
      <Route path="/vote" element={<VotePage />} />
      <Route path="/wins" element={<WinsPage />} />
      <Route path="/referral" element={<ReferralLanding />} />
      <Route path="/r/:code" element={<ReferralLanding />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  React.useEffect(() => { initAnalytics(); }, []);
  return (
    <HelmetProvider>
      {/* Site-wide JSON-LD only. Per-page <SEO> handles title/description/canonical/OG. */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(organizationJsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteJsonLd)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(softwareAppJsonLd)}
        </script>
      </Helmet>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <CurrencyProvider>
              <CommandPaletteProvider>
                <NativeBootstrap />
                <PromoBanner />
                <Router />
                <GuidedTour />
                <SanctaChatbot />
                <ContactFAB />
                <DirectChatNotifierMount />
                <InstallPrompt />
                <ConsentBanner />
                <Toaster position="top-right" richColors closeButton />
              </CommandPaletteProvider>
            </CurrencyProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
